import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  { id: "investor", label: "Investor Inquiry" },
  { id: "business", label: "Business Development" },
  { id: "general", label: "General Inquiry" },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    inquiry_type: "investor",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you for your inquiry. We'll be in touch shortly.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        inquiry_type: "investor",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubscribing(true);

    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success("Successfully subscribed to our newsletter.");
      setNewsletterEmail("");
    } catch (error) {
      if (error.response?.data?.detail === "Already subscribed") {
        toast.info("You're already subscribed to our newsletter.");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <main data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
          >
            Let's Start a Conversation
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-slate-400 font-light text-base md:text-lg max-w-2xl"
          >
            Whether you're an investor seeking opportunities or a company exploring 
            partnership possibilities, we'd like to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      data-testid="contact-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="luxury-input w-full"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="luxury-input w-full"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      data-testid="contact-phone-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="luxury-input w-full"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      data-testid="contact-company-input"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="luxury-input w-full"
                      placeholder="Company Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                    Inquiry Type *
                  </label>
                  <select
                    data-testid="contact-type-select"
                    value={formData.inquiry_type}
                    onChange={(e) => setFormData({ ...formData, inquiry_type: e.target.value })}
                    required
                    className="luxury-input w-full bg-transparent cursor-pointer"
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type.id} value={type.id} className="bg-[#0F172A]">
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-500 text-xs uppercase tracking-wider mb-2">
                    Message *
                  </label>
                  <textarea
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={6}
                    className="luxury-input w-full resize-none"
                    placeholder="Tell us about your interest..."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-8 py-4 transition-all duration-300 btn-gold-glow disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 loading-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={14} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-12"
            >
              {/* Info Cards */}
              <div className="glass-card p-8">
                <h3 className="text-white text-lg mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Headquarters</p>
                      <p className="text-white text-sm mt-1">
                        104 W 40th Street, 5th Fl<br />
                        New York, NY 10018
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Email</p>
                      <p className="text-white text-sm mt-1">info@unleashedventures.com</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Newsletter */}
              <div className="glass-card p-8" data-testid="newsletter-section">
                <h3 className="text-white text-lg mb-2">Stay Informed</h3>
                <p className="text-slate-400 text-sm mb-6">
                  Subscribe to receive investment insights and company updates.
                </p>
                <form onSubmit={handleNewsletterSubscribe} className="flex gap-3">
                  <input
                    type="email"
                    data-testid="newsletter-email-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    className="luxury-input flex-1"
                  />
                  <button
                    type="submit"
                    data-testid="newsletter-submit-btn"
                    disabled={subscribing}
                    className="bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-6 py-4 transition-all duration-300 disabled:opacity-50"
                  >
                    {subscribing ? "..." : "Subscribe"}
                  </button>
                </form>
                <p className="text-slate-600 text-xs mt-4 leading-relaxed">
                  By subscribing, you agree to receive marketing communications from Unleashed Ventures. 
                  You may unsubscribe at any time. View our <a href="/privacy" className="text-slate-500 hover:text-amber-400 underline">Privacy Policy</a>.
                </p>
              </div>

              {/* Response Time */}
              <div className="border border-white/10 p-6">
                <p className="text-slate-500 text-sm font-accent italic">
                  "We typically respond to inquiries within 24-48 business hours. 
                  For urgent matters, please indicate so in your message."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
